import React, { useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import DataTable, { Pagination } from "../../components/admin/DataTable";

const STATUSES = ["placed", "confirmed", "processing", "shipped", "delivered", "cancelled", "returned", "refunded"];

export default function AdminOrders() {
  const [filters, setFilters] = useState({ q: "", status: "", payment_status: "", seller_id: "" });
  const [sellers, setSellers] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sellers/admin/all").then(({ data }) => setSellers(data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/orders", { params });
      setData(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.payment_status, filters.seller_id, page]);

  const updateStatus = async (id, status) => {
    if (!window.confirm(`Change order status to ${status}?`)) return;
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "order_number", label: "Order", render: (r) => <span className="font-mono text-xs">{r.order_number || r.id.slice(0, 8)}</span> },
    { key: "user_id", label: "User", render: (r) => <span className="text-xs font-mono">{r.user_id.slice(0, 8)}</span> },
    { key: "item_count", label: "Items", render: (r) => r.item_count ?? (r.items?.length || 0) },
    { key: "total", label: "Total", render: (r) => money(r.total) },
    { key: "payment_status", label: "Payment", render: (r) => <span className="uppercase text-xs">{r.payment_status}</span> },
    {
      key: "status", label: "Status",
      render: (r) => (
        <select
          className="kb-input !py-1 !px-2 !text-xs"
          value={r.status}
          onChange={(e) => updateStatus(r.id, e.target.value)}
          data-testid={`order-status-${r.id}`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
      ),
    },
    { key: "created_at", label: "Placed", render: (r) => (r.created_at || "").slice(0, 10) },
  ];

  return (
    <div data-testid="admin-orders-page">
      <div className="overline mb-2">Fulfilment</div>
      <h1 className="font-display text-4xl mb-8">All Orders ({data.total})</h1>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-4 mb-4 grid md:grid-cols-5 gap-3">
        <input className="kb-input md:col-span-2" placeholder="Search by order number / id…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} data-testid="filter-q" />
        <select className="kb-input" value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }} data-testid="filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="kb-input" value={filters.payment_status} onChange={(e) => { setFilters({ ...filters, payment_status: e.target.value }); setPage(1); }} data-testid="filter-payment">
          <option value="">All payments</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="refunded">Refunded</option>
        </select>
        <select className="kb-input" value={filters.seller_id} onChange={(e) => { setFilters({ ...filters, seller_id: e.target.value }); setPage(1); }} data-testid="filter-seller">
          <option value="">All sellers</option>
          {sellers.map((s) => <option key={s.id} value={s.id}>{s.shop_name}</option>)}
        </select>
      </div>

      <DataTable columns={columns} rows={data.items} loading={loading} emptyLabel="No orders match" testid="orders-table" />
      <Pagination page={page} limit={20} total={data.total} onPage={setPage} />
    </div>
  );
}
